import { useAuthStore } from '@/stores/authStore';

export type FieldAccessLevel = 'none' | 'read' | 'write';

/**
 * Hook to check if the current user has specific permissions.
 *
 * Three layers of access control:
 *  1. **Permission codes** (`hasPermission`, `hasAnyPermission`) — module-
 *     level grants like `accounting.revenue.read`.
 *  2. **Field-level access** (`getFieldAccess`, `hasFieldAccess`,
 *     `hasFieldWrite`) — RBAC v2 field categories like `compensation`,
 *     `medical`, `disciplinary`, `bank_account`. Empty when
 *     `PERMISSION_ENGINE_V2` is OFF on the backend.
 *  3. **Scopes** (`hasScope`) — row-level scopes like `self_only`,
 *     `direct_department`, `gl_account_range`.
 *
 * Tenants on the legacy engine see empty field_permissions + empty scopes,
 * so callers should treat those as "no field-level restriction" and fall
 * through to the legacy module check.
 */
export function usePermission() {
  const { user, companies, activeCompanyId, getPermissions, getFieldPermissions, getScopes } =
    useAuthStore();

  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);

  // Backend-derived flag (Company / Group / legacy System Administrator).
  // Single source of truth for "user can manage tenant-wide settings" —
  // every hub page uses `isTopAdmin = isSystemAdmin || isGroupAdmin` as its
  // bypass, so `isSystemAdmin` has to reflect this or tenant admins on
  // solo/non-group-level companies get their tabs filtered out when their
  // role has no explicit permissions (e.g. legacy roles on tenants where
  // the Sprint-1 rename migration hasn't yet applied).
  const isTenantAdmin = activeCompany?.is_tenant_admin === true;
  const isSystemAdmin = isTenantAdmin;
  const isGroupAdmin = activeCompany?.is_group_level ?? false;
  const roleName = activeCompany?.role_name ?? '';
  const hasMultipleCompanies = companies.length > 1;
  const hasGroupAccess = isGroupAdmin;

  const permissions = getPermissions();
  const permissionSet = new Set(permissions);

  const hasPermission = (code: string): boolean => permissionSet.has(code);
  const hasAnyPermission = (codes: string[]): boolean =>
    codes.some((code) => permissionSet.has(code));
  const hasAllPermissions = (codes: string[]): boolean =>
    codes.every((code) => permissionSet.has(code));

  // RBAC v2 — field-level grants and scopes.
  const fieldPermissions = getFieldPermissions();
  const scopes = new Set(getScopes());

  /** Returns the access level granted for ``fieldCategory``. */
  const getFieldAccess = (fieldCategory: string): FieldAccessLevel => {
    return fieldPermissions[fieldCategory] ?? 'none';
  };

  /** True when the user has any access (read or write) to ``fieldCategory``. */
  const hasFieldAccess = (fieldCategory: string): boolean => {
    return getFieldAccess(fieldCategory) !== 'none';
  };

  /** True when the user has write-level access to ``fieldCategory``. */
  const hasFieldWrite = (fieldCategory: string): boolean => {
    return getFieldAccess(fieldCategory) === 'write';
  };

  /** True when the named scope (e.g. ``self_only``) is active for the user. */
  const hasScope = (scopeType: string): boolean => scopes.has(scopeType);

  return {
    isSystemAdmin,
    isTenantAdmin,
    isGroupAdmin,
    roleName,
    hasMultipleCompanies,
    hasGroupAccess,
    isAuthenticated: !!user,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions: Array.from(permissionSet),
    // RBAC v2
    fieldPermissions,
    getFieldAccess,
    hasFieldAccess,
    hasFieldWrite,
    hasScope,
    activeScopes: Array.from(scopes),
  };
}
